import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import DocumentUpload from "../elements/DocumentUpload";
import Modal from "../elements/Modal";
import { ReactComponent as LeftArrow } from "../../resources/icons/left_arrow.svg";
import downArrow from "../../resources/down_arrow.png";
import SubtaskList from "../elements/SubtaskList";
import { UploadedDocument } from "../../types";
import { useAuthContext } from "../../contexts/authContext";
import ProjectDataSection from "../elements/ProjectDataSection";
import ProjectParticipants from "../elements/participants/ProjectParticipants";
import { getDocumentPresignedUrl, getProjectById } from "../../api/projects";
import PDFPreviewModal from "../elements/modals/PDFPreviewModal";

const documentTypes = [
  { title: "Building Permit", key: "PERMIT" },
  { title: "AKT 14", key: "AKT14" },
  { title: "AKT 15", key: "AKT15" },
  { title: "AKT 16", key: "AKT16" },
  { title: "Other Documents", key: "OTHER" },
];

const getStatusStyle = (status: string) => {
  switch (status.toLowerCase()) {
    case "ongoing":
      return "text-[#B54708] bg-[#FFFAEB] border-[#FEDF89]";
    case "open":
      return "text-[#1672EC] bg-[#F0F9FF] border-[#B9E6FE]";
    case "draft":
      return "text-[#344054] bg-[#F9FAFB] border-[#EAECF0]";
    case "completed":
      return "text-[#067647] bg-[#ECFDF3] border-[#ABEFC6]";
    case "cancelled":
      return "text-[#B42318] bg-[#FEF3F2] border-[#FECDCA]";
    default:
      return "text-[#344054] bg-[#F9FAFB] border-[#D0D5DD]";
  }
};

const ProjectEdit: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [project, setProject] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [documents, setDocuments] = useState<Record<string, any[]>>({});
  const [newDocs, setNewDocs] = useState<Record<string, any[]>>({});
  const [openDocs, setOpenDocs] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteKey, setDeleteKey] = useState<string | null>(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitSectionName, setLimitSectionName] = useState("");

  const { user, role } = useAuthContext();

  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [previewName, setPreviewName] = useState<string>("");
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const onPreview = async (id: string, name: string) => {
    try {
      const { data, error } = await getDocumentPresignedUrl(id);
      if (error || !data?.file?.url) {
        alert("Нямаш достъп до този документ или той е изтрит.");
        return;
      }

      if (!data?.file?.url) {
        alert("Документът не е качен коректно или липсва.");
        return;
      }
      setPreviewUrl(data.file.url);
      setPreviewName(name);
      setShowPreviewModal(true);
    } catch {}
  };

  const canDeleteDocument = useCallback(
    (doc: UploadedDocument) => {
      if (doc.isNew) return true;
      if (role === "ADMIN") return true;
      if (doc.uploader?.id === user?.id) return true;
      return false;
    },
    [role, user?.id]
  );

  const fetchProject = useCallback(async () => {
    if (projectId) {
      setLoading(true);
      const { data, error } = await getProjectById(parseInt(projectId));
      if (error) {
        navigate("/projects");
        return;
      }
      if (data) {
        setProject(data);
        const grouped = (data.documents || []).reduce((acc: any, doc: any) => {
          acc[doc.type] = acc[doc.type] || [];
          acc[doc.type].push({ ...doc, canDelete: canDeleteDocument(doc) });
          return acc;
        }, {});
        setDocuments(grouped);
        setNewDocs({});
      }
      setLoading(false);
    }
  }, [projectId, navigate, canDeleteDocument]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  useEffect(() => {
    if (
      !loading &&
      project &&
      (project.status.toLowerCase() === "completed" ||
        project.status.toLowerCase() === "cancelled")
    ) {
      navigate(`/projects/${projectId}/finished`, { replace: true });
    }
  }, [loading, project, navigate, projectId, location.pathname]);

  const handleAddNewDocs = (type: string, files: any[]) => {
    const current = documents[type]?.length || 0;
    const adding = newDocs[type]?.length || 0;
    const total = current + adding + files.length;
    if (total > 20) {
      const section = documentTypes.find((d) => d.key === type);
      setLimitSectionName(section?.title || type);
      setShowLimitModal(true);
      return;
    }
    setNewDocs((prev) => ({
      ...prev,
      [type]: [...(prev[type] || []), ...files],
    }));
  };

  const handleRemoveNewDoc = (type: string, name: string) => {
    setNewDocs((prev) => ({
      ...prev,
      [type]: prev[type].filter((f) => f.name !== name),
    }));
  };

  const handleRequestDelete = (id: string, key: string) => {
    setDeleteId(id);
    setDeleteKey(key);
  };

  const confirmDelete = async () => {
    if (!deleteId || !deleteKey) return;
    const token = localStorage.getItem("token");
    await axios.delete(
      `${process.env.REACT_APP_URL}/projects/documents/${deleteId}`,
      { headers: { Authorization: `Bearer ${token}`, "X-Client-Type": "web" } }
    );
    setDocuments((prev) => ({
      ...prev,
      [deleteKey]: prev[deleteKey].filter((d) => d.id !== deleteId),
    }));
    setDeleteId(null);
    setDeleteKey(null);
  };

  const handleUpdate = async () => {
    setUpdating(true);
    const token = localStorage.getItem("token");
    const fileUploadErrs: string[] = [];

    for (const key of Object.keys(newDocs)) {
      const uploads = newDocs[key];
      for (const doc of uploads) {
        const formData = new FormData();
        formData.append("file", doc.file);
        try {
          await axios.post(
            `${process.env.REACT_APP_URL}/projects/documents?projectId=${projectId}&documentType=${key}`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "X-Client-Type": "web",
              },
            }
          );
        } catch (error: any) {
          fileUploadErrs.push(error.message);
        }
      }
    }

    if (fileUploadErrs.length > 0) {
      alert("File upload failed.");
    }

    await fetchProject();
    setUpdating(false);
    navigate(-1);
  };

  if (loading || !project) return <div>Loading…</div>;

  return (
    <div className="flex">
      <div className="flex-1 p-6 ml-[17.5rem] flex flex-col">
        <div className="flex items-center mb-4 border-b pb-4">
          <button
            onClick={() => (location.key ? navigate(-1) : navigate("/"))}
            className="mr-3"
          >
            <LeftArrow className="w-6 h-6" />
          </button>
          <h1 className="text-2xl font-bold text-wrap">{project.title}</h1>
          <span
            className={`ml-4 text-sm font-medium px-3 py-1 border rounded-full ${getStatusStyle(
              project.status
            )}`}
          >
            {project.status}
          </span>
        </div>

        <div className="flex justify-between gap-6 mb-6">
          <div className="flex flex-1">
            <div>
              <h2 className="text-lg font-semibold mb-1">
                Project details summary
              </h2>
              <p className="max-w-[500px] max-h-32 overflow-auto pr-2 whitespace-pre-wrap break-words text-[#475467]">
                {project.summary}
              </p>
              <ProjectParticipants
                supervisor={project.supervisor}
                contractor={project.contractor}
                assignor={project.assignor}
              />
            </div>
          </div>
          <ProjectDataSection project={project} />
        </div>

        <div
          className="flex items-center cursor-pointer mb-4"
          onClick={() => setOpenDocs(!openDocs)}
        >
          <h2 className="font-semibold">Documents</h2>
          <img
            src={downArrow}
            alt="Toggle"
            className={`w-5 h-5 ml-2 transform ${openDocs ? "" : "rotate-180"}`}
          />
        </div>

        {openDocs &&
          documentTypes.map(({ title, key }) => (
            <DocumentUpload
              key={key}
              title={title}
              type={key}
              docs={documents[key] || []}
              newDocs={newDocs[key] || []}
              onAddNewDocs={(files) => handleAddNewDocs(key, files)}
              onRemoveNewDoc={(name) => handleRemoveNewDoc(key, name)}
              onRequestDelete={(id) => handleRequestDelete(id, key)}
              onPreview={onPreview}
            />
          ))}

        <PDFPreviewModal
          open={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          url={previewUrl}
          name={previewName}
        />

        <p className="text-xl my-4">Subtasks</p>
        <SubtaskList subtasks={project.subTasks || []} />

        <div className="flex justify-start mt-auto pt-4">
          <button
            onClick={handleUpdate}
            className={`bg-blue-600 text-white px-6 py-2 rounded ${
              updating ? "opacity-50 cursor-not-allowed" : ""
            }`}
            disabled={updating}
          >
            {updating ? "Updating..." : "Update Project"}
          </button>
        </div>

        <Modal isOpen={!!deleteId}>
          <div className="p-6">
            <h2 className="text-lg font-bold mb-2">Delete Document</h2>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to delete this document? This action cannot
              be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteId(null)}
                className="px-4 py-1 bg-gray-200 rounded"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-1 bg-red-600 text-white rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </Modal>

        <Modal isOpen={showLimitModal}>
          <div className="p-6">
            <h2 className="text-lg font-bold mb-2">Document Limit Reached</h2>
            <p className="text-sm text-gray-600 mb-4">
              You have reached the maximum of 20 documents for
              <strong>{limitSectionName}</strong>.
            </p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowLimitModal(false)}
                className="px-4 py-1 bg-blue-600 text-white rounded"
              >
                OK
              </button>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default ProjectEdit;
